import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { Disposable, toDisposable, dispose } from 'vscode/vscode/vs/base/common/lifecycle';
import { Extensions } from 'vscode/vscode/vs/workbench/common/contributions';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import { IHostService } from 'vscode/vscode/vs/workbench/services/host/browser/host.service';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { IWorkspaceContextService } from 'vscode/vscode/vs/platform/workspace/common/workspace.service';
import { IExtensionService } from 'vscode/vscode/vs/workbench/services/extensions/common/extensions.service';
import { RunOnceScheduler } from 'vscode/vscode/vs/base/common/async';
import { isEqual } from 'vscode/vscode/vs/base/common/resources';
import { isNative, isMacintosh, isLinux } from 'vscode/vscode/vs/base/common/platform';
import { IDialogService } from 'vscode/vscode/vs/platform/dialogs/common/dialogs.service';
import { IWorkbenchEnvironmentService } from 'vscode/vscode/vs/workbench/services/environment/common/environmentService.service';
import { IProductService } from 'vscode/vscode/vs/platform/product/common/productService.service';

var SettingsChangeRelauncher_1;
const _moduleId = "vs/workbench/contrib/relauncher/browser/relauncher.contribution";
let SettingsChangeRelauncher = class SettingsChangeRelauncher extends Disposable {
    static { SettingsChangeRelauncher_1 = this; }
    static { this.SETTINGS = [
        "window.titleBarStyle" ,
        'window.nativeTabs',
        'window.nativeFullScreen',
        'window.clickThroughInactive',
        'update.mode',
        'editor.accessibilitySupport',
        'security.workspace.trust.enabled',
        'workbench.enableExperiments',
        '_extensionsGallery.enablePPE',
        'security.restrictUNCAccess'
    ]; }
    constructor(hostService, configurationService, productService, dialogService) {
        super();
        this.hostService = hostService;
        this.configurationService = configurationService;
        this.productService = productService;
        this.dialogService = dialogService;
        this.titleBarStyle = ( (new ChangeObserver('string')));
        this.nativeTabs = ( (new ChangeObserver('boolean')));
        this.nativeFullScreen = ( (new ChangeObserver('boolean')));
        this.clickThroughInactive = ( (new ChangeObserver('boolean')));
        this.updateMode = ( (new ChangeObserver('string')));
        this.workspaceTrustEnabled = ( (new ChangeObserver('boolean')));
        this.experimentsEnabled = ( (new ChangeObserver('boolean')));
        this.enablePPEExtensionsGallery = ( (new ChangeObserver('boolean')));
        this.restrictUNCAccess = ( (new ChangeObserver('boolean')));
        this.onConfigurationChange(undefined);
        this._register(this.configurationService.onDidChangeConfiguration(e => this.onConfigurationChange(e)));
    }
    onConfigurationChange(e) {
        if (e && !( (SettingsChangeRelauncher_1.SETTINGS.some(key => e.affectsConfiguration(key))))) {
            return;
        }
        let changed = false;
        function processChanged(didChange) {
            changed = changed || didChange;
        }
        const config = this.configurationService.getValue();
        if (isNative) {
            processChanged(((config.window.titleBarStyle === "native"  || config.window.titleBarStyle === "custom") ) && this.titleBarStyle.handleChange(config.window?.titleBarStyle));
            processChanged(isMacintosh && this.nativeTabs.handleChange(config.window?.nativeTabs));
            processChanged(isMacintosh && this.nativeFullScreen.handleChange(config.window?.nativeFullScreen));
            processChanged(isMacintosh && this.clickThroughInactive.handleChange(config.window?.clickThroughInactive));
            processChanged(this.updateMode.handleChange(config.update?.mode));
            if (isLinux && typeof config.editor?.accessibilitySupport === 'string' && config.editor.accessibilitySupport !== this.accessibilitySupport) {
                this.accessibilitySupport = config.editor.accessibilitySupport;
                if (this.accessibilitySupport === 'on') {
                    changed = true;
                }
            }
            processChanged(this.workspaceTrustEnabled.handleChange(config?.security?.workspace?.trust?.enabled));
            processChanged(this.restrictUNCAccess.handleChange(config?.security?.restrictUNCAccess));
        }
        processChanged(this.experimentsEnabled.handleChange(config.workbench?.enableExperiments));
        processChanged(this.productService.quality !== 'stable' && this.enablePPEExtensionsGallery.handleChange(config._extensionsGallery?.enablePPE));
        if (changed && e && e.source !== 7 ) {
            this.doConfirm(isNative ?
                ( localizeWithPath(
                    _moduleId,
                    0,
                    "A setting has changed that requires a restart to take effect."
                )) :
                ( localizeWithPath(
                    _moduleId,
                    1,
                    "A setting has changed that requires a reload to take effect."
                )), isNative ?
                ( localizeWithPath(
                    _moduleId,
                    2,
                    "Press the restart button to restart {0} and enable the setting.",
                    this.productService.nameLong
                )) :
                ( localizeWithPath(
                    _moduleId,
                    3,
                    "Press the reload button to reload {0} and enable the setting.",
                    this.productService.nameLong
                )), isNative ?
                ( localizeWithPath(_moduleId, 4, "&&Restart")) :
                ( localizeWithPath(_moduleId, 5, "&&Reload")), () => this.hostService.restart());
        }
    }
    async doConfirm(message, detail, primaryButton, confirmedFn) {
        if (this.hostService.hasFocus) {
            const { confirmed } = await this.dialogService.confirm({ message, detail, primaryButton });
            if (confirmed) {
                confirmedFn();
            }
        }
    }
};
SettingsChangeRelauncher = SettingsChangeRelauncher_1 = ( (__decorate([
    ( (__param(0, IHostService))),
    ( (__param(1, IConfigurationService))),
    ( (__param(2, IProductService))),
    ( (__param(3, IDialogService)))
], SettingsChangeRelauncher)));
class ChangeObserver {
    static create(typeName) {
        return (
             (new ChangeObserver(typeName))
        );
    }
    constructor(typeName) {
        this.typeName = typeName;
        this.lastValue = undefined;
    }
    handleChange(value) {
        if (typeof value === this.typeName && value !== this.lastValue) {
            this.lastValue = value;
            return true;
        }
        return false;
    }
}
let WorkspaceChangeExtHostRelauncher = class WorkspaceChangeExtHostRelauncher extends Disposable {
    constructor(contextService, extensionService, hostService, environmentService) {
        super();
        this.contextService = contextService;
        this.extensionHostRestarter = this._register(( (new RunOnceScheduler(async () => {
            if (!!environmentService.extensionTestsLocationURI) {
                return;
            }
            if (environmentService.remoteAuthority) {
                hostService.reload();
            }
            else if (isNative) {
                const stopped = await extensionService.stopExtensionHosts(( localizeWithPath(
                    _moduleId,
                    6,
                    "Restarting extension host due to a workspace folder change."
                )));
                if (stopped) {
                    extensionService.startExtensionHosts();
                }
            }
        }, 10))));
        this.contextService.getCompleteWorkspace()
            .then(workspace => {
            this.firstFolderResource = workspace.folders.length > 0 ? workspace.folders[0].uri : undefined;
            this.handleWorkbenchState();
            this._register(this.contextService.onDidChangeWorkbenchState(() => setTimeout(() => this.handleWorkbenchState())));
        });
        this._register(toDisposable(() => {
            this.onDidChangeWorkspaceFoldersUnbind?.dispose();
        }));
    }
    handleWorkbenchState() {
        if (this.contextService.getWorkbenchState() === 3 ) {
            const workspace = this.contextService.getWorkspace();
            this.firstFolderResource = workspace.folders.length > 0 ? workspace.folders[0].uri : undefined;
            if (!this.onDidChangeWorkspaceFoldersUnbind) {
                this.onDidChangeWorkspaceFoldersUnbind = this.contextService.onDidChangeWorkspaceFolders(() => this.onDidChangeWorkspaceFolders());
            }
        }
        else {
            dispose(this.onDidChangeWorkspaceFoldersUnbind);
            this.onDidChangeWorkspaceFoldersUnbind = undefined;
        }
    }
    onDidChangeWorkspaceFolders() {
        const workspace = this.contextService.getWorkspace();
        const newFirstFolderResource = workspace.folders.length > 0 ? workspace.folders[0].uri : undefined;
        if (!isEqual(this.firstFolderResource, newFirstFolderResource)) {
            this.firstFolderResource = newFirstFolderResource;
            this.extensionHostRestarter.schedule();
        }
    }
};
WorkspaceChangeExtHostRelauncher = ( (__decorate([
    ( (__param(0, IWorkspaceContextService))),
    ( (__param(1, IExtensionService))),
    ( (__param(2, IHostService))),
    ( (__param(3, IWorkbenchEnvironmentService)))
], WorkspaceChangeExtHostRelauncher)));
const workbenchRegistry = ( (Registry.as(Extensions.Workbench)));
workbenchRegistry.registerWorkbenchContribution(SettingsChangeRelauncher, 3 );
workbenchRegistry.registerWorkbenchContribution(WorkspaceChangeExtHostRelauncher, 3 );

export { SettingsChangeRelauncher, WorkspaceChangeExtHostRelauncher };
